import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { container } from "tsyringe";
import { AnalyticsService } from "../../services/analytics";

class MediaToCompress {
    objectUrl: string | undefined;
    name: string | undefined;
    file: File | undefined;
}

export enum CompressionLevel {
    minimum,
    default,
    maximum,
}

export interface CompressionState {
    compressionLevel: CompressionLevel,
    mediaToCompress: MediaToCompress[];
    compressionInProgress: boolean
}

const initialState: CompressionState = {
    compressionLevel: CompressionLevel.default,
    mediaToCompress: [],
    compressionInProgress: false,
}

export const CompressionSlice = createSlice({
    name: 'compression',
    initialState,
    reducers: {
        removeMedia: (state, action: PayloadAction<MediaToCompress>) => {
            state.mediaToCompress = state.mediaToCompress.filter(
                media => media.name !== action.payload.name
            );
        },
        onCompressionLevelChanged: (
            state,
            action: PayloadAction<CompressionLevel>
        ) => {
            state.compressionLevel = action.payload;
        },
        onCompressionStateChanged: (state, action: PayloadAction<boolean>) => {
            state.compressionInProgress = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(addMediaAsync.fulfilled, (state, action) => {
            if (action.payload === undefined) {
                return;
            }

            state.mediaToCompress = [];

            state.mediaToCompress.push({
                name: action.payload.name,
                objectUrl: action.payload.objectUrl,
                file: action.payload.file,
            });
        });
    },

});

export const addMediaAsync = createAsyncThunk(
    "compression/addMediaAsync",
    async (file: File | undefined) => {
        const analyticsService = container.resolve(AnalyticsService);
        analyticsService.LogEvent({
            category: "Button Click",
            action: "Load image/video",
            label: "Compression",
        });
        if (file === undefined) return;

        var objectUrl = URL.createObjectURL(file);
        return {
            name: file.name,
            objectUrl: objectUrl,
            file: file,
        };
    });

export const compressAsync = createAsyncThunk<
    void,
    void,
    { state: RootState }
>(
    "compression/compressAsync",
    async (_, { dispatch, getState }) => {

        // Lazy-load @ffmpeg/util
        const { fetchFile } = await import('@ffmpeg/util');

        // Lazy-load browser-image-compression
        const imageCompression = (await import('browser-image-compression')).default;

        const state = getState().compression;
        var file = state.mediaToCompress[0].file;
        if (!file) return;

        dispatch(onCompressionStateChanged(true));
        /*Image compression*/
        if (file.type.startsWith("image/")) {

            try {

                var previewUrl = "";
                switch (state.compressionLevel) {
                    case CompressionLevel.minimum:
                        {
                            const compressedFile = await imageCompression(file, {
                                maxSizeMB: 2,
                                maxWidthOrHeight: 1920,
                                useWebWorker: true,
                                initialQuality: 0.9,
                            });
                            previewUrl = URL.createObjectURL(compressedFile);
                        }
                        break;
                    case CompressionLevel.default:
                        {
                            const compressedFile = await imageCompression(file, {
                                maxSizeMB: 1,
                                maxWidthOrHeight: 1280,
                                useWebWorker: true,
                                initialQuality: 0.75,
                            });
                            previewUrl = URL.createObjectURL(compressedFile);
                        }
                        break;
                    case CompressionLevel.maximum:
                        {
                            const compressedFile = await imageCompression(file, {
                                maxSizeMB: 0.5,
                                maxWidthOrHeight: 800,
                                useWebWorker: true,
                                initialQuality: 0.6,
                            });
                            previewUrl = URL.createObjectURL(compressedFile);
                        }
                        break;
                }

                const a = document.createElement("a");
                document.body.appendChild(a);
                a.href = previewUrl;
                a.download = file.name;
                a.click();
            } catch (e) {
                var test = 0;
            } finally {
                dispatch(onCompressionStateChanged(false));
            }

            return;
        }


        /*Video compression*/
        try {
            dispatch(onCompressionStateChanged(true));

            const FFmpeg = await import("@ffmpeg/ffmpeg");

            const ffmpeg = new FFmpeg.FFmpeg();

            ffmpeg.on('log', ({ message }) => {
                console.warn(message);
            });

            await ffmpeg.load();

            await ffmpeg.writeFile('input.mp4', await fetchFile(file));


            // run command
            const video = document.createElement("video");
            video.preload = "metadata";

            // Create URL from file blob
            if (!file) {
                throw new Error("No file provided");
            }

            const videoUrl = URL.createObjectURL(file);
            video.src = videoUrl;

            video.onloadedmetadata = async () => {
                URL.revokeObjectURL(videoUrl); // Clean up

                try {
                    switch (state.compressionLevel) {
                        case CompressionLevel.minimum:
                            var scaleFactor = 0.85;
                            var targetWidth = Math.floor(video.videoWidth * scaleFactor);
                            var targetHeight = Math.floor(video.videoHeight * scaleFactor);

                            await ffmpeg.exec([
                                "-i", "input.mp4",
                                "-vf", `scale=${targetWidth}:${targetHeight}`,
                                "-vcodec", "mpeg4",
                                "-qscale:v", "6",
                                "-c:a", "copy",
                                "-r", "30",
                                "output.mp4"
                            ]);
                            break;
                        case CompressionLevel.default:
                            var scaleFactor = 0.65;
                            var targetWidth = Math.floor(video.videoWidth * scaleFactor);
                            var targetHeight = Math.floor(video.videoHeight * scaleFactor);

                            await ffmpeg.exec([
                                "-i", "input.mp4",
                                "-vf", `scale=${targetWidth}:${targetHeight}`,
                                "-vcodec", "mpeg4",
                                "-qscale:v", "10",
                                "-c:a", "copy",
                                "-r", "30",
                                "output.mp4"
                            ]);
                            break;
                        case CompressionLevel.maximum:
                            var scaleFactor = 0.40;
                            var targetWidth = Math.floor(video.videoWidth * scaleFactor);
                            var targetHeight = Math.floor(video.videoHeight * scaleFactor);

                            await ffmpeg.exec([
                                "-i", "input.mp4",
                                "-vf", `scale=${targetWidth}:${targetHeight}`,
                                "-vcodec", "mpeg4",
                                "-qscale:v", "15",
                                "-c:a", "copy",
                                "-r", "30",
                                "output.mp4"
                            ]);
                            break;
                    }

                    const fileData = await ffmpeg.readFile("output.mp4");

                    // Normalize to Uint8Array
                    let outputBytes: Uint8Array;
                    if (typeof fileData === "string") {
                        outputBytes = new TextEncoder().encode(fileData);
                    } else {
                        outputBytes = fileData as Uint8Array;
                    }

                    // Create a clean ArrayBuffer slice that matches the actual data range
                    const arrayBuffer: ArrayBuffer = outputBytes.buffer.slice(
                        outputBytes.byteOffset,
                        outputBytes.byteOffset + outputBytes.byteLength
                    ) as ArrayBuffer;

                    const blob = new Blob([arrayBuffer], { type: 'video/mp4' });
                    const url = URL.createObjectURL(blob);

                    const a = document.createElement("a");
                    document.body.appendChild(a);
                    a.href = url;
                    a.download = file!.name;
                    a.click();
                } finally {
                    dispatch(onCompressionStateChanged(false));
                }


            };

        } catch (e) {
            var test = 0;
        } finally {

        }
    });

export const { removeMedia, onCompressionLevelChanged, onCompressionStateChanged } = CompressionSlice.actions;

export const selectState = (state: RootState) => state.compression;

export default CompressionSlice.reducer;